import type { ReactNode } from "react"
import {
  useMemo,
  useState,
  useCallback,
} from "react"
import type { CountrySelectionContextValue } from "./CountrySelectionContext"
import { CountrySelectionContext } from "./CountrySelectionContext"
import {
  getLocalStorageCountryCodeSelection,
  setLocalStorageCountryCodeSelection,
} from "./LocalStorageCountryCodeSelection"
import { useCountry } from "src/hooks"

interface Props {
  oauthCountryCode?: string;
  children?: ReactNode;
}

/*
 * this context keeps track of the user's country selection on public pages
 *
 * the countryCode may be preset with the following precedence:
 * - props.oauthCountryCode
 * - previously selected countryCode (from local storage)
 *
 * when a user takes an action to confirm their country selection the
 * confirmCountrySelection function should be called
 */
export function CountrySelectionContextProvider(props: Props) {
  const { oauthCountryCode } = props

  const lastSelectedCountryCode = useMemo(
    () => getLocalStorageCountryCodeSelection(),
    [],
  )

  const [
    hasUserConfirmedCountrySelection,
    setHasUserConfirmedCountry,
  ] = useState<boolean>(!!oauthCountryCode)

  const [
    countryCode,
    setCountryCode,
  ] = useState<CountrySelectionContextValue["countryCode"]>(
    oauthCountryCode ?? lastSelectedCountryCode ?? "",
  )

  const country = useCountry(`${countryCode}`)

  const updateCountryCode = useCallback(
    (newCountryCode: string) => {
      setCountryCode(newCountryCode)
      setHasUserConfirmedCountry(false)
      setLocalStorageCountryCodeSelection(newCountryCode)
    },
    [],
  )

  const confirmCountrySelection = useCallback(
    () => {
      setHasUserConfirmedCountry(true)
    },
    [],
  )

  const value = useMemo(
    () => ({
      country,
      countryCode: country?.country_code ?? "",
      setCountryCode: updateCountryCode,
      hasUserConfirmedCountrySelection,
      confirmCountrySelection,
    }),
    [
      country,
      updateCountryCode,
      hasUserConfirmedCountrySelection,
      confirmCountrySelection,
    ],
  )

  return (
    <CountrySelectionContext.Provider value={value}>
      {props.children}
    </CountrySelectionContext.Provider>
  )
}
